import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  memo,
} from "react";
import { FaAngleDown, FaBoxes, FaTrash } from "react-icons/fa";
import { IoIosWarning } from "react-icons/io";
import { MdAttachMoney, MdTrendingUp } from "react-icons/md";
import { LuPackage } from "react-icons/lu";
import { TbCurrencyPeso } from "react-icons/tb";

import { useQuery, useAppSelector } from "features/config/hooks";
import { selectstockOrderGlobalTrigger } from "../slices/stock-order.slice";
import {
  useActionAvailability,
  totalCompute,
  moneyFormatter,
} from "./stock-ordering-utils";
import { BatchInlineRating } from "./batch-product-rating-display";
import { StockOrderHandleQuantity } from ".";

interface PreviewProductDataProps {
  idx: number;
  productData: any;
  setProductData: (data: any) => void;
  statusId: number;
  onRatingClick?: (productId: string) => void;
  onToggleRemoveProduct?: (productId: string, index: number) => void;
  isRemoved?: boolean;
  overrideHandleQuantity?: boolean;
}

// Helper functions for styling
const getCardStyles = (
  isRemoved: boolean,
  quantityMismatch: boolean,
  showMismatch: boolean
) => {
  if (isRemoved) return "border-l-gray-400 bg-gray-50";
  if (quantityMismatch) return "border-l-red-500 bg-red-25";
  if (showMismatch) return "border-l-amber-500 bg-amber-25";
  return "border-l-blue-500 bg-white";
};

const getTextStyles = (
  isRemoved: boolean,
  variant: "primary" | "secondary" | "muted" = "primary"
) => {
  if (isRemoved) {
    return {
      primary: "text-gray-500",
      secondary: "text-gray-400",
      muted: "text-gray-400",
    }[variant];
  }

  return {
    primary: "text-gray-900",
    secondary: "text-gray-600",
    muted: "text-gray-500",
  }[variant];
};

const getMetricBoxStyles = (isRemoved: boolean) =>
  `rounded-md p-1.5 backdrop-blur-sm border min-h-[50px] flex flex-col justify-center ${
    isRemoved
      ? "bg-gray-200/70 border-gray-300/20"
      : "bg-white/70 border-white/20"
  }`;

const getDesktopMetricBoxStyles = (isRemoved: boolean) =>
  `flex flex-col items-center rounded-lg p-2 backdrop-blur-sm border w-full max-w-[100px] ${
    isRemoved
      ? "bg-gray-200/70 border-gray-300/20"
      : "bg-white/70 border-white/20"
  }`;

// Sub-components
const ProductImage = React.memo(
  ({
    isRemoved,
    size = "mobile",
  }: {
    isRemoved: boolean;
    size?: "mobile" | "desktop";
  }) => {
    const sizeClasses =
      size === "mobile" ? "w-8 h-8 sm:w-10 sm:h-10" : "w-12 h-12";
    const iconSizeClasses =
      size === "mobile" ? "w-3 h-3 sm:w-4 sm:h-4" : "w-5 h-5";

    return (
      <div
        className={`${sizeClasses} bg-gradient-to-br rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 ${
          isRemoved ? "from-gray-300 to-gray-400" : "from-gray-200 to-gray-300"
        }`}
      >
        <LuPackage
          className={`${iconSizeClasses} ${getTextStyles(
            isRemoved,
            "secondary"
          )}`}
        />
      </div>
    );
  }
);

const ProductHeader = React.memo(
  ({
    productData,
    isRemoved,
    onRatingClick,
  }: {
    productData: any;
    isRemoved: boolean;
    onRatingClick?: (productId: string) => void;
  }) => (
    <div className="flex flex-wrap items-center gap-1 mb-1">
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold ${
          isRemoved ? "bg-gray-200 text-gray-500" : "bg-gray-100 text-gray-800"
        }`}
      >
        {productData.product_id}
      </span>
      <BatchInlineRating
        productId={productData.product_id}
        onNavigate={
          onRatingClick
            ? () => onRatingClick(productData.product_id)
            : undefined
        }
      />
    </div>
  )
);

const ActionButton = React.memo(
  ({
    onClick,
    icon: Icon,
    className,
    title,
    children,
  }: {
    onClick: (e: React.MouseEvent) => void;
    icon?: React.ComponentType<{ className?: string }>;
    className: string;
    title?: string;
    children?: React.ReactNode;
  }) => (
    <button
      onClick={onClick}
      className={`rounded-full flex-shrink-0 flex items-center justify-center will-change-transform ${className}`}
      style={{
        transition:
          "background-color 150ms ease-out, transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.transform = "scale3d(1.05, 1.05, 1)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.transform = "scale3d(1, 1, 1)")
      }
      title={title}
    >
      {Icon && <Icon className="w-2.5 h-2.5" />}
      {children}
    </button>
  )
);

const MetricBox = React.memo(
  ({
    icon: Icon,
    label,
    value,
    isRemoved,
    showWarning = false,
    width,
    isMobile = true,
    valueColor,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: React.ReactNode;
    isRemoved: boolean;
    showWarning?: boolean;
    width?: string;
    isMobile?: boolean;
    valueColor?: string;
  }) => {
    const boxStyles = isMobile
      ? getMetricBoxStyles(isRemoved)
      : getDesktopMetricBoxStyles(isRemoved);
    const iconSize = isMobile ? "w-2.5 h-2.5" : "w-2.5 h-2.5";

    return (
      <div className={`${boxStyles} ${width || (isMobile ? "w-[30%]" : "")}`}>
        <div
          className={`flex items-center ${
            isMobile ? "justify-center" : ""
          } space-x-1 mb-1`}
        >
          <Icon
            className={`${iconSize} ${
              isRemoved ? "text-gray-400" : "text-blue-600"
            }`}
          />
          <span
            className={`text-xs font-medium ${getTextStyles(
              isRemoved,
              "secondary"
            )}`}
          >
            {label}
          </span>
          {showWarning && !isRemoved && (
            <IoIosWarning className="w-2.5 h-2.5 text-amber-500" />
          )}
        </div>
        <div
          className={`${isMobile ? "text-xs" : "text-sm"} font-bold ${
            isMobile ? "pt-1 block" : ""
          } ${isMobile ? "text-center" : ""} ${
            valueColor || getTextStyles(isRemoved)
          }`}
        >
          {value}
        </div>
      </div>
    );
  }
);

const PreviewProductDataComponent = (props: PreviewProductDataProps) => {
  const query = useQuery();

  // Permission and action hooks
  const stockClerkAccess = useActionAvailability(1).permission;
  const dispatcherAccess = useActionAvailability(6).permission;
  const { actionAvailable, processId } = useActionAvailability();

  const globalTriggerState = useAppSelector(selectstockOrderGlobalTrigger);

  const {
    productData,
    setProductData,
    idx,
    statusId,
    onRatingClick,
    onToggleRemoveProduct,
    isRemoved = false,
    overrideHandleQuantity,
  } = props;

  const { processEditProductButton } = globalTriggerState;

  // State management
  const [qty, setQty] = useState(productData.order_qty);
  const [total, setTotal] = useState(
    Number(totalCompute(productData.productCost, qty))
  );
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Use ref to track previous values and prevent unnecessary calls
  const prevDataRef = useRef({
    qty: qty ?? 0,
    idx,
    total: Number(total?.toFixed(2)),
  });

  // Memoized calculations
  const computeForQtyPerOrder = useMemo(
    () =>
      productData.uom_multiplier *
      (productData.order_qty ?? productData.orderQty),
    [productData.uom_multiplier, productData.order_qty, productData.orderQty]
  );

  const mismatchWarning = useMemo(
    () => ({
      isMismatch:
        productData.orderQtyAfter === null
          ? qty !== productData.order_qty
          : qty !== productData.orderQtyAfter,
      color: "text-amber-500",
    }),
    [qty, productData.order_qty, productData.orderQtyAfter]
  );

  const quantityMismatch = useMemo(() => {
    if (processId && processId > 5) {
      if (productData.dispatch_qty !== null) {
        return (
          productData.dispatch_qty !== productData.delivered_qty ||
          productData.orderQtyAfter !== productData.delivered_qty
        );
      } else {
        return productData.commited_qty !== productData.delivered_qty;
      }
    }
    return false;
  }, [processId, productData]);

  const disableHandleQuantityByPermission = useMemo(() => {
    if (processId === 6 || processId === 1) return false;
    return (
      (dispatcherAccess && actionAvailable) ||
      (stockClerkAccess && actionAvailable)
    );
  }, [processId, dispatcherAccess, stockClerkAccess, actionAvailable]);

  // Get subtotal value based on process and permissions
  const getSubtotalValue = useMemo(() => {
    if (processId === 4 && actionAvailable && dispatcherAccess) {
      return `₱${moneyFormatter(productData.total_cost, 2)}`;
    }
    if (processId === 5 && actionAvailable && stockClerkAccess) {
      return `₱${moneyFormatter(productData.total_cost, 2)}`;
    }
    return `₱${moneyFormatter(
      productData.total_cost ?? productData.cost * productData.orderQty,
      2
    )}`;
  }, [
    processId,
    actionAvailable,
    dispatcherAccess,
    stockClerkAccess,
    productData.dispatch_subtotal,
    productData.receive_subtotal,
    productData.total_cost,
    productData.cost,
    productData.orderQty,
  ]);

  const getSubtotalColor = useMemo(() => {
    if (isRemoved) return "text-gray-400";
    if (processId === 4 && actionAvailable && dispatcherAccess)
      return "text-green-600";
    if (processId === 5 && actionAvailable && stockClerkAccess)
      return "text-purple-600";
    return "text-blue-600";
  }, [
    isRemoved,
    processId,
    actionAvailable,
    dispatcherAccess,
    stockClerkAccess,
  ]);

  // Event handlers
  const handleNewQtyValue = useCallback(
    (newQty: number) => {
      setQty(newQty);
      setTotal(Number(totalCompute(productData.productCost, newQty)));
    },
    [productData.productCost]
  );

  const handleToggleRemoveProduct = useCallback(() => {
    if (onToggleRemoveProduct) {
      onToggleRemoveProduct(productData.product_id || productData.id, idx);
    }
  }, [onToggleRemoveProduct, productData.product_id, productData.id, idx]);

  const handleCardClick = useCallback(() => {
    if (isRemoved && onToggleRemoveProduct) {
      handleToggleRemoveProduct();
    }
  }, [isRemoved, onToggleRemoveProduct, handleToggleRemoveProduct]);

  const handleExpandClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isRemoved) {
        setOpen(!open);
      }
    },
    [isRemoved, open]
  );

  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleToggleRemoveProduct();
    },
    [handleToggleRemoveProduct]
  );

  // Fixed useEffect - only call setProductData when values actually change
  useEffect(() => {
    const newData = {
      index: idx,
      quantity: qty ?? 0,
      subTotal: Number(total?.toFixed(2)),
    };

    // Only call setProductData if values have actually changed
    const prev = prevDataRef.current;
    if (
      prev.qty !== newData.quantity ||
      prev.idx !== newData.index ||
      prev.total !== newData.subTotal
    ) {
      prevDataRef.current = {
        qty: newData.quantity,
        idx: newData.index,
        total: newData.subTotal,
      };
      setProductData(newData);
    }
  }, [qty, idx, total]); // Removed setProductData from dependencies

  // Render quantity display based on permissions
  const renderQuantityDisplay = useCallback(
    (isMobile = true) => {
      if (isRemoved) {
        return (
          <span
            className={`${
              isMobile ? "text-xs" : "text-base"
            } font-bold text-gray-400`}
          >
            {moneyFormatter(qty ?? productData.orderQty)}
          </span>
        );
      }

      if (
        (actionAvailable && dispatcherAccess) ||
        (actionAvailable && stockClerkAccess)
      ) {
        if (
          disableHandleQuantityByPermission &&
          !isRemoved &&
          !overrideHandleQuantity
        ) {
          return (
            <StockOrderHandleQuantity
              setNewCurrentValue={handleNewQtyValue}
              currentValue={qty}
              maxQuantity={
                productData.orderQtyAfter ? productData.orderQtyAfter : 9999
              }
            />
          );
        }
        return (
          <span
            className={`${
              isMobile ? "text-xs" : "text-base"
            } font-bold ${getTextStyles(isRemoved)}`}
          >
            {moneyFormatter(qty ?? productData.orderQty)}
          </span>
        );
      }

      return (
        <span
          className={`${
            isMobile ? "text-xs" : "text-base"
          } font-bold ${getTextStyles(isRemoved)}`}
        >
          {moneyFormatter(productData.order_qty ?? productData.orderQty)}
        </span>
      );
    },
    [
      isRemoved,
      qty,
      productData.orderQty,
      productData.order_qty,
      productData.orderQtyAfter,
      actionAvailable,
      dispatcherAccess,
      stockClerkAccess,
      disableHandleQuantityByPermission,
      handleNewQtyValue,
    ]
  );

  return (
    <React.Fragment>
      {/* Main Product Card */}
      <div
        className={`relative group mb-2 will-change-transform ${
          isRemoved ? "opacity-60 cursor-pointer" : ""
        }`}
        style={{
          transform:
            isHovered && !isRemoved
              ? "scale3d(1.01, 1.01, 1)"
              : "scale3d(1, 1, 1)",
          transition:
            "transform 200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-out",
          contain: "layout style paint",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
      >
        {/* Removed Overlay */}
        {isRemoved && (
          <div
            className="absolute inset-0 bg-gray-900/20 rounded-lg z-10 flex items-center justify-center"
            style={{ transition: "opacity 150ms ease-out" }}
          >
            <div className="bg-white rounded px-2 py-1 shadow border-l-2 border-orange-500">
              <span className="text-xs font-medium text-gray-700 flex items-center">
                <FaTrash className="w-2 h-2 mr-1 text-orange-500" />
                Marked for Removal - Click to Restore
              </span>
            </div>
          </div>
        )}

        {/* Card Container */}
        <div
          className={`relative overflow-hidden rounded-lg border-l-2 ${getCardStyles(
            isRemoved,
            quantityMismatch,
            mismatchWarning.isMismatch
          )}`}
          style={{
            boxShadow:
              isHovered && !isRemoved
                ? "0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
            transition: "box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/60 to-white/40 pointer-events-none" />

          {/* Main Content */}
          <div className="relative p-2 sm:p-2 lg:p-3">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              {/* Header Row */}
              <div className="flex items-center mb-2 space-x-1.5">
                <span
                  className={`flex-shrink-0 text-xs font-medium w-4 ${getTextStyles(
                    isRemoved,
                    "secondary"
                  )}`}
                >
                  {idx + 1}
                </span>

                <ProductImage isRemoved={isRemoved} size="mobile" />

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <ProductHeader
                    productData={productData}
                    isRemoved={isRemoved}
                    onRatingClick={onRatingClick}
                  />
                  <h2
                    className={`text-xs font-bold mb-1 leading-tight ${
                      isRemoved ? "text-gray-500 line-through" : "text-gray-900"
                    }`}
                  >
                    {productData.product_name}
                  </h2>
                  <div
                    className={`text-xs ${getTextStyles(
                      isRemoved,
                      "secondary"
                    )}`}
                  >
                    <span>
                      UOM:{" "}
                      <span className="font-medium">{productData.uom}</span> •
                      {/* Qty/order:{" "}
                      <span className="font-medium">
                        {computeForQtyPerOrder}
                      </span> */}
                    </span>
                  </div>
                </div>

                {/* Action Buttons - Mobile */}
                {!isRemoved && (
                  <div className="flex items-center space-x-1">
                    {processEditProductButton && onToggleRemoveProduct && (
                      <ActionButton
                        onClick={handleRemoveClick}
                        icon={FaTrash}
                        className="p-1 w-6 h-6 bg-red-100 text-red-600"
                        title="Mark for Removal"
                      />
                    )}
                    <ActionButton
                      onClick={handleExpandClick}
                      className="p-1 w-6 h-6 bg-gray-100 text-gray-600"
                    >
                      <div
                        style={{
                          transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          transition:
                            "transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        <FaAngleDown className="w-3 h-3" />
                      </div>
                    </ActionButton>
                  </div>
                )}
              </div>

              {/* Metrics Row - Mobile */}
              <div className="flex items-start justify-between">
                <MetricBox
                  icon={FaBoxes}
                  label="Qty"
                  value={renderQuantityDisplay(true)}
                  isRemoved={isRemoved}
                  showWarning={mismatchWarning.isMismatch}
                  width="w-[30%]"
                />

                <MetricBox
                  icon={MdAttachMoney}
                  label="Unit"
                  value={`₱${moneyFormatter(
                    productData.productCost ?? productData.cost
                  )}`}
                  isRemoved={isRemoved}
                  width="w-[30%]"
                />

                <MetricBox
                  icon={MdTrendingUp}
                  label="Total"
                  value={moneyFormatter(getSubtotalValue, 2)}
                  isRemoved={isRemoved}
                  valueColor={getSubtotalColor}
                  width="w-[30%]"
                />
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:block">
              <div className="flex items-center justify-between space-x-3">
                <span
                  className={`flex-shrink-0 text-sm font-medium w-6 ${getTextStyles(
                    isRemoved,
                    "secondary"
                  )}`}
                >
                  {idx + 1}
                </span>

                {/* Product Info Grid */}
                <div className="grid grid-cols-6 gap-2 flex-1 items-center">
                  <div className="flex justify-center">
                    <ProductImage isRemoved={isRemoved} size="desktop" />
                  </div>

                  {/* Product Name & Info */}
                  <div className="col-span-2 min-w-0">
                    <ProductHeader
                      productData={productData}
                      isRemoved={isRemoved}
                      onRatingClick={onRatingClick}
                    />
                    <h2
                      className={`text-sm font-bold mb-0.5 leading-tight ${
                        isRemoved
                          ? "text-gray-500 line-through"
                          : "text-gray-900"
                      }`}
                    >
                      {productData.product_name}
                    </h2>
                    <div
                      className={`text-xs ${getTextStyles(
                        isRemoved,
                        "secondary"
                      )}`}
                    >
                      <span>UOM: {productData.uom}</span>
                      <span className="ml-3">
                        Qty/order: {moneyFormatter(computeForQtyPerOrder)}
                      </span>
                    </div>
                  </div>

                  {/* Desktop Metrics */}
                  <div className="flex justify-center">
                    <MetricBox
                      icon={FaBoxes}
                      label="Quantity"
                      value={renderQuantityDisplay(false)}
                      isRemoved={isRemoved}
                      showWarning={mismatchWarning.isMismatch}
                      isMobile={false}
                    />
                  </div>

                  <div className="flex justify-center">
                    <MetricBox
                      icon={TbCurrencyPeso}
                      label="Unit Cost"
                      value={`₱${moneyFormatter(
                        productData.productCost ?? productData.cost,
                        2
                      )}`}
                      isRemoved={isRemoved}
                      isMobile={false}
                    />
                  </div>

                  <div className="flex justify-center">
                    <MetricBox
                      icon={MdTrendingUp}
                      label="Subtotal"
                      value={getSubtotalValue}
                      isRemoved={isRemoved}
                      valueColor={getSubtotalColor}
                      isMobile={false}
                    />
                  </div>
                </div>

                {/* Action Buttons - Desktop */}
                {!isRemoved && (
                  <div className="flex items-center space-x-1">
                    {processEditProductButton && onToggleRemoveProduct && (
                      <ActionButton
                        onClick={handleRemoveClick}
                        icon={FaTrash}
                        className="p-2 bg-red-100 text-red-600"
                        title="Mark for Removal"
                      />
                    )}
                    <ActionButton
                      onClick={handleExpandClick}
                      className="p-2 bg-gray-100 text-gray-600"
                    >
                      <div
                        style={{
                          transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          transition:
                            "transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        <FaAngleDown className="w-4 h-4" />
                      </div>
                    </ActionButton>
                  </div>
                )}
              </div>
            </div>

            {/* Expandable Details */}
            {!isRemoved && (
              <div
                style={{
                  transform: open ? "scaleY(1)" : "scaleY(0)",
                  transformOrigin: "top",
                  opacity: open ? 1 : 0,
                  height: open ? "auto" : "0",
                  overflow: "hidden",
                  transition:
                    "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                  willChange: "transform, opacity",
                }}
              >
                <div className="bg-white/50 rounded-lg border border-white/30 backdrop-blur-sm mt-2">
                  <div className="p-2 sm:p-3">
                    <h3 className="text-xs font-semibold text-gray-900 mb-2">
                      Detailed Information
                    </h3>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {/* Quantity Details */}
                      <div className="space-y-2">
                        <h4 className="text-xs font-semibold text-gray-700 border-b border-gray-200 pb-0.5">
                          Quantity Tracking
                        </h4>
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">
                              Order Quantity (after Place order)
                            </span>
                            <span className="text-xs font-semibold text-gray-900">
                              {moneyFormatter(
                                productData.orderQtyAfter ??
                                  productData.order_qty ??
                                  productData.orderQty
                              )}
                            </span>
                          </div>
                          {statusId > 4 && productData.commited_qty && (
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">
                                Committed Quantity
                              </span>
                              <span className="text-xs font-semibold text-gray-900">
                                {moneyFormatter(productData.commited_qty)}
                              </span>
                            </div>
                          )}
                          {statusId > 4 && productData.dispatch_qty && (
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">
                                Dispatched Quantity
                              </span>
                              <span className="text-xs font-semibold text-gray-900">
                                {moneyFormatter(productData.dispatch_qty)}
                              </span>
                            </div>
                          )}
                          {statusId > 5 && (
                            <div
                              className={`flex justify-between items-center ${
                                quantityMismatch
                                  ? "text-red-600 font-bold underline underline-offset-2"
                                  : ""
                              }`}
                            >
                              <span className="text-xs">
                                Delivered Quantity
                              </span>
                              <span className="text-xs font-semibold">
                                {moneyFormatter(productData.delivered_qty)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Cost Breakdown */}
                      <div className="space-y-2">
                        <h4 className="text-xs font-semibold text-gray-700 border-b border-gray-200 pb-0.5">
                          Cost Breakdown
                        </h4>
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">
                              Product order subtotal (after Place order)
                            </span>
                            <span className="text-xs font-semibold text-gray-900">
                              ₱
                              {moneyFormatter(
                                productData.orderSubTotalAfter ??
                                  productData.total_cost ??
                                  productData.cost * productData.orderQty,
                                2
                              )}
                            </span>
                          </div>
                          {statusId > 4 && productData.committed_subtotal && (
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">
                                Product order committed subtotal
                              </span>
                              <span className="text-xs font-semibold text-gray-900">
                                ₱
                                {moneyFormatter(
                                  productData.committed_subtotal,
                                  2
                                )}
                              </span>
                            </div>
                          )}
                          {statusId > 4 && productData.dispatch_subtotal && (
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">
                                Product order dispatch subtotal
                              </span>
                              <span className="text-xs font-semibold text-gray-900">
                                ₱
                                {moneyFormatter(
                                  productData.dispatch_subtotal,
                                  2
                                )}
                              </span>
                            </div>
                          )}
                          {statusId > 5 && (
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">
                                Product order delivered subtotal
                              </span>
                              <span className="text-xs font-semibold text-gray-900">
                                ₱
                                {moneyFormatter(
                                  productData.receive_subtotal,
                                  2
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export const PreviewProductData = memo(PreviewProductDataComponent);
